import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { transfersService } from '../services/transfersService';
import { providersService } from '../services/providersService';
import { driversService } from '../services/driversService';
import { customersService } from '../services/customersService';
import { providerGroupsService, ProviderGroup } from '../services/providerGroupsService';
import api from '../services/api';
import { Transfer, Guest, UserRole, TripType, Currency, Provider, Driver, VehicleType, ProviderType, Customer } from '../types';
import { GuestModal } from '../components/GuestModal';
import { RootState } from '../store/store';
import { GooglePlacesAutocomplete } from '../components/GooglePlacesAutocomplete';

type TransferFormData = Omit<Transfer, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'netProfit'>;

export const EditTransferPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const user = useSelector((state: RootState) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [loadingTransfer, setLoadingTransfer] = useState(true);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(Currency.TRY);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [assignmentType, setAssignmentType] = useState<'driver' | 'provider' | 'none'>('none');
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [enableProviderPriceEdit, setEnableProviderPriceEdit] = useState(false);
  const [providerGroups, setProviderGroups] = useState<ProviderGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');

  // Meet & Greet Fee state
  const [hasMeetGreet, setHasMeetGreet] = useState(false);

  const canEditTransfer = user?.role === UserRole.MANAGER ||
                          user?.role === UserRole.ORG_ADMIN ||
                          user?.role === UserRole.PLATFORM_ADMIN ||
                          user?.role === UserRole.PROVIDER_USER ||
                          user?.role === UserRole.PROVIDER_MANAGER ||
                          (user?.role === UserRole.STAFF && user?.transferPermissions?.includes('edit'));

  const canEditPricing = user?.role === UserRole.MANAGER ||
                         user?.role === UserRole.ORG_ADMIN ||
                         user?.role === UserRole.PLATFORM_ADMIN;

  const canDeleteTransfer = user?.role === UserRole.MANAGER ||
                            user?.role === UserRole.ORG_ADMIN ||
                            user?.role === UserRole.PLATFORM_ADMIN ||
                            (user?.role === UserRole.STAFF && user?.transferPermissions?.includes('delete'));

  useEffect(() => {
    const loadData = async () => {
      try {
        const [providersData, driversData, vehicleTypesData, customersData, groupsData] = await Promise.all([
          providersService.getAll(true),
          driversService.getAll(true),
          api.get('/vehicle-types?activeOnly=true').then(res => res.data),
          customersService.getAll(),
          providerGroupsService.getAll(),
        ]);
        setProviders(providersData);
        setDrivers(driversData);
        setVehicleTypes(vehicleTypesData);
        setCustomers(customersData);
        setProviderGroups(groupsData);
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, []);

  const currencySymbols: Record<Currency, string> = {
    [Currency.TRY]: '₺',
    [Currency.EUR]: '€',
    [Currency.USD]: '$',
    [Currency.GBP]: '£',
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<TransferFormData>();

  const selectedTripType = watch('tripType');
  const watchPickupLocation = watch('pickupLocation');
  const watchDropoffLocation = watch('dropoffLocation');
  const watchStatus = watch('status');
  const watchProviderPrice = watch('providerPrice');
  const watchAssignedProviderId = watch('assignedProviderId');
  const watchWhatsappDriverName = watch('whatsappDriverName');

  // Load transfer data
  useEffect(() => {
    const loadTransfer = async () => {
      if (!id) {
        navigate('/app/transfers');
        return;
      }

      try {
        setLoadingTransfer(true);
        const transfer = await transfersService.getById(id);

        reset({
          transferDate: transfer.transferDate,
          transferTime: transfer.transferTime.substring(0, 5),
          returnTime: transfer.returnTime ? transfer.returnTime.substring(0, 5) : undefined,
          tripType: transfer.tripType || TripType.ARRIVAL,
          tcPassport: transfer.tcPassport,
          flightCode: transfer.flightCode,
          country: transfer.country,
          pickupLocation: transfer.pickupLocation,
          dropoffLocation: transfer.dropoffLocation,
          vehicleType: transfer.vehicleType,
          passengerCount: transfer.passengerCount,
          specialNotes: transfer.specialNotes,
          customerPrice: transfer.customerPrice || undefined,
          customerId: transfer.customerId || undefined,
          providerPrice: transfer.providerPrice || undefined,
          providerPaid: transfer.providerPaid || false,
          extraExpenses: transfer.extraExpenses || undefined,
          meetGreetFee: transfer.meetGreetFee || undefined,
          assignedProviderId: transfer.assignedProviderId || transfer.assignedOrganizationId,
          assignedDriverId: transfer.assignedDriverId,
          whatsappDriverName: transfer.whatsappDriverName || undefined,
          whatsappDriverPhone: transfer.whatsappDriverPhone || undefined,
          providerDriverName: transfer.providerDriverName || undefined,
          providerDriverPhone: transfer.providerDriverPhone || undefined,
          status: transfer.status,
        });

        setHasMeetGreet(!!transfer.meetGreetFee && Number(transfer.meetGreetFee) > 0);
        setSelectedCustomerId(transfer.customerId || '');
        setSelectedGroupId(transfer.poolGroupId || '');

        if (transfer.guests && transfer.guests.length > 0) {
          setGuests(transfer.guests);
        }

        if (transfer.currency) {
          setSelectedCurrency(transfer.currency);
        }

        if (transfer.assignedDriverId) {
          setAssignmentType('driver');
        } else if (transfer.assignedProviderId || transfer.assignedOrganizationId) {
          setAssignmentType('provider');
          if (transfer.assignedProviderId) {
            const provider = providers.find((p: Provider) => p.id === transfer.assignedProviderId);
            if (provider) setSelectedProvider(provider);
          }
        } else {
          setAssignmentType('none');
        }
      } catch (error: any) {
        console.error('Failed to load transfer:', error);
        toast.error(t('transfers.failedToLoad'));
        navigate('/app/transfers');
      } finally {
        setLoadingTransfer(false);
      }
    };

    loadTransfer();
  }, [id, navigate, reset]);

  const handleGuestsAdded = (newGuests: Guest[]) => {
    setGuests(newGuests);
    toast.success(t('guest.guestsUpdated'));
  };

  const handleRemoveGuest = (index: number) => {
    const newGuests = guests.filter((_, i) => i !== index);
    setGuests(newGuests);
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm(t('transfers.confirmDelete'))) return;
    try {
      await transfersService.delete(id);
      toast.success(t('transfers.transferDeleted'));
      navigate('/app/transfers');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('transfers.failedToDelete'));
    }
  };

  const onSubmit = async (data: TransferFormData) => {
    if (!id) return;

    if (data.status === 'completed') {
      const hasProviderPrice = data.providerPrice != null && !isNaN(data.providerPrice as any) && Number(data.providerPrice) > 0;
      if (!hasProviderPrice) {
        toast.error(t('transfers.completedRequiresPrices'));
        return;
      }
    }

    try {
      setLoading(true);

      const cleanedGuests = guests.length > 0
        ? guests.map(({ name, phone, email, tcPassport }) => ({
            name,
            phone: phone || undefined,
            email: email || undefined,
            tcPassport: tcPassport || undefined,
          }))
        : undefined;

      const cleanedData = {
        ...data,
        currency: selectedCurrency,
        providerPrice: isNaN(data.providerPrice as any) ? undefined : data.providerPrice,
        extraExpenses: isNaN(data.extraExpenses as any) ? undefined : data.extraExpenses,
        meetGreetFee: hasMeetGreet ? (isNaN(data.meetGreetFee as any) ? undefined : data.meetGreetFee) : 0,
        tcPassport: data.tcPassport || undefined,
        assignedProviderId: data.assignedProviderId || undefined,
        assignedDriverId: data.assignedDriverId || undefined,
        whatsappDriverName: data.whatsappDriverName || undefined,
        whatsappDriverPhone: data.whatsappDriverPhone || undefined,
        providerDriverName: data.providerDriverName || undefined,
        providerDriverPhone: data.providerDriverPhone || undefined,
        returnTime: data.returnTime || undefined,
        flightCode: data.flightCode || undefined,
        country: data.country || undefined,
        specialNotes: data.specialNotes || undefined,
        guests: cleanedGuests,
        customerPrice: isNaN(data.customerPrice as any) ? undefined : data.customerPrice,
        customerId: selectedCustomerId || undefined,
        poolGroupId: assignmentType === 'none' ? (selectedGroupId || null) : null,
        customerName: undefined,
        customerPhone: undefined,
        customerEmail: undefined,
      };

      await transfersService.update(id, cleanedData);
      toast.success(t('transfers.transferUpdated'));
      navigate('/app/transfers');
    } catch (error: any) {
      let errorMessage = t('transfers.failedToUpdate');
      if (error.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          errorMessage = error.response.data.message.join(', ');
        } else {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!canEditTransfer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('transfers.accessDenied')}</h2>
          <p className="text-gray-600">{t('transfers.noPermissionToEdit')}</p>
        </div>
      </div>
    );
  }

  if (loadingTransfer) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">{t('transfers.loadingTransfer')}</p>
        </div>
      </div>
    );
  }

  // Provider users see simplified form with transfer details
  if (user?.role === UserRole.PROVIDER_USER) {
    const currentStatus = watch('status');
    const isCompleted = currentStatus === 'completed';
    const currentProviderPrice = watch('providerPrice');
    const currentDriverName = watch('providerDriverName');
    const currentDriverPhone = watch('providerDriverPhone');

    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('transfers.editTransfer')}</h1>
          <p className="text-gray-600 mt-1">{t('transfers.updateProviderInfo')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Provider Input Form */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg p-6 border-2 border-blue-300">
              <div className="flex items-center mb-4">
                <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <h2 className="text-xl font-bold text-blue-900">{t('transfers.yourInformation')}</h2>
              </div>
              <p className="text-sm text-blue-700 mb-6">{t('transfers.enterPriceAndDriverDetails')}</p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Provider Price */}
                <div className="bg-white rounded-lg p-4 shadow">
                  <label className="block text-base font-bold text-gray-900 mb-2">
                    {t('transfers.providerPrice')} * {isCompleted && <span className="text-red-600 text-sm">({t('transfers.locked')})</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      {...register('providerPrice', { required: t('transfers.providerPriceRequired') })}
                      disabled={isCompleted}
                      className={`w-full px-4 py-3 text-2xl font-bold border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isCompleted ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                      placeholder="0.00"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-2xl font-bold text-gray-500">TL</span>
                  </div>
                  {errors.providerPrice && (
                    <p className="text-red-600 text-sm mt-2 font-medium">{errors.providerPrice.message}</p>
                  )}
                  {isCompleted && (
                    <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">
                      {t('transfers.priceLockedAfterComplete')}
                    </p>
                  )}
                  {!isCompleted && currentProviderPrice && (
                    <p className="text-sm text-green-600 mt-2 bg-green-50 p-2 rounded">
                      {t('transfers.priceSet', { price: currentProviderPrice })}
                    </p>
                  )}
                </div>

                {/* Driver Information */}
                <div className="bg-white rounded-lg p-4 shadow space-y-4">
                  <h3 className="font-bold text-gray-900 mb-3">{t('transfers.driverInformation')}</h3>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('transfers.driverName')} * {isCompleted && <span className="text-red-600 text-sm">({t('transfers.locked')})</span>}
                    </label>
                    <input
                      type="text"
                      {...register('providerDriverName', { required: t('transfers.driverNameRequired') })}
                      disabled={isCompleted}
                      className={`w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isCompleted ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder={t('transfers.placeholders.driverFullName')}
                    />
                    {errors.providerDriverName && (
                      <p className="text-red-600 text-sm mt-1">{errors.providerDriverName.message}</p>
                    )}
                    {!isCompleted && currentDriverName && !errors.providerDriverName && (
                      <p className="text-sm text-green-600 mt-1">✓ {currentDriverName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('transfers.driverPhone')} {isCompleted && <span className="text-red-600 text-sm">({t('transfers.locked')})</span>}
                    </label>
                    <input
                      type="tel"
                      {...register('providerDriverPhone')}
                      disabled={isCompleted}
                      className={`w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isCompleted ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder={t('transfers.placeholders.driverPhoneOptional')}
                    />
                    {!isCompleted && currentDriverPhone && (
                      <p className="text-sm text-green-600 mt-1">✓ {currentDriverPhone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('transfers.licensePlate')} {isCompleted && <span className="text-red-600 text-sm">({t('transfers.locked')})</span>}
                    </label>
                    <input
                      type="text"
                      {...register('providerPlate')}
                      disabled={isCompleted}
                      className={`w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isCompleted ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder="34 ABC 123 (optional)"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    {loading ? t('transfers.saving') : t('transfers.saveChanges')}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/app/transfers')}
                    className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Transfer Details - Read-only */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('transfers.transferDetails')}
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">{t('transfers.dateAndTime')}</p>
                  <p className="font-semibold">{watch('transferDate')} {watch('transferTime')?.substring(0, 5)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">{t('transfers.pickup')}</p>
                  <p className="font-medium">{watch('pickupLocation')}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">{t('transfers.dropoff')}</p>
                  <p className="font-medium">{watch('dropoffLocation')}</p>
                </div>
                {watch('tripType') && (
                  <div>
                    <p className="text-gray-500 text-xs">{t('transfers.transferType')}</p>
                    <p className="font-medium">{watch('tripType') === 'arrival' ? t('transfers.arrival') : t('transfers.departure')}</p>
                  </div>
                )}
                {watch('vehicleType') && (
                  <div>
                    <p className="text-gray-500 text-xs">{t('transfers.vehicleType')}</p>
                    <p className="font-medium">{watch('vehicleType')}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500 text-xs">{t('transfers.passengerCount')}</p>
                  <p className="font-medium">{watch('passengerCount')} {t('transfers.passengers')}</p>
                </div>
                {watch('flightCode') && (
                  <div>
                    <p className="text-gray-500 text-xs">{t('transfers.flightCode')}</p>
                    <p className="font-medium">{watch('flightCode')}</p>
                  </div>
                )}
                {guests && guests.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">{t('transfers.guests')} ({guests.length})</p>
                    <div className="space-y-1">
                      {guests.map((guest, idx) => (
                        <div key={idx} className="bg-gray-50 p-2 rounded text-xs">
                          <p className="font-medium">{guest.name}</p>
                          {guest.phone && <p className="text-gray-600">{guest.phone}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {watch('specialNotes') && (
                  <div>
                    <p className="text-gray-500 text-xs">{t('transfers.specialNotes')}</p>
                    <p className="font-medium text-xs bg-yellow-50 p-2 rounded">{watch('specialNotes')}</p>
                  </div>
                )}
                <div className="pt-3 border-t">
                  <p className="text-gray-500 text-xs">{t('common.status')}</p>
                  <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                    currentStatus === 'available' ? 'bg-emerald-100 text-emerald-800' :
                    currentStatus === 'claimed' ? 'bg-yellow-100 text-yellow-800' :
                    currentStatus === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                    currentStatus === 'in_progress' ? 'bg-purple-100 text-purple-800' :
                    currentStatus === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {currentStatus}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('transfers.editTransfer')}</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">{t('transfers.editSubtitle')}</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">

          {/* 1. Transfer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.transferType')}
            </label>
            <select
              {...register('tripType')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value={TripType.ARRIVAL}>{t('transfers.arrival')}</option>
              <option value={TripType.DEPARTURE}>{t('transfers.departure')}</option>
            </select>
          </div>

          {/* 1b. Kaynak Firma */}
          {canEditPricing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kaynak Firma
              </label>
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
              >
                <option value="">Seçin (opsiyonel)</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* 2. Transfer Date & Time */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('transfers.transferDate')} *
              </label>
              <input
                type="date"
                {...register('transferDate', { required: t('transfers.dateRequired') })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
              />
              {errors.transferDate && (
                <p className="text-red-500 text-xs mt-1">{errors.transferDate.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('transfers.transferTime')} *
              </label>
              <input
                type="time"
                {...register('transferTime', { required: t('transfers.timeRequired') })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
              />
              {errors.transferTime && (
                <p className="text-red-500 text-xs mt-1">{errors.transferTime.message}</p>
              )}
            </div>
          </div>

          {/* 3. Pickup Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.pickupLocation')} *
            </label>
            <GooglePlacesAutocomplete
              value={watchPickupLocation || ''}
              onChange={(value) => setValue('pickupLocation', value, { shouldValidate: true })}
              placeholder={t('transfers.placeholders.pickupExample')}
              error={!!errors.pickupLocation}
            />
            <input type="hidden" {...register('pickupLocation', { required: t('transfers.pickupRequired') })} />
            {errors.pickupLocation && (
              <p className="text-red-500 text-sm mt-1">{errors.pickupLocation.message}</p>
            )}
          </div>

          {/* 4. Dropoff Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.dropoffLocation')} *
            </label>
            <GooglePlacesAutocomplete
              value={watchDropoffLocation || ''}
              onChange={(value) => setValue('dropoffLocation', value, { shouldValidate: true })}
              placeholder={t('transfers.placeholders.dropoffExample')}
              error={!!errors.dropoffLocation}
            />
            <input type="hidden" {...register('dropoffLocation', { required: t('transfers.dropoffRequired') })} />
            {errors.dropoffLocation && (
              <p className="text-red-500 text-sm mt-1">{errors.dropoffLocation.message}</p>
            )}
          </div>

          {/* 5. Return Time (only for Departure) */}
          {selectedTripType === TripType.DEPARTURE && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('transfers.returnTime')}
              </label>
              <input
                type="time"
                {...register('returnTime')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* 6. Flight Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.flightCode')}
            </label>
            <input
              type="text"
              {...register('flightCode')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('transfers.placeholders.flightCodeExample')}
            />
          </div>

          {/* 7. Vehicle Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.vehicleType')}
            </label>
            <select
              {...register('vehicleType')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('transfers.selectVehicleType')}</option>
              {vehicleTypes.map((vehicleType) => (
                <option key={vehicleType.id} value={vehicleType.name}>
                  {vehicleType.name}
                </option>
              ))}
            </select>
          </div>

          {/* 8. Passenger Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.passengerCount')}
            </label>
            <input
              type="number"
              min="1"
              {...register('passengerCount', {
                valueAsNumber: true,
                min: { value: 1, message: t('transfers.passengerCountMin') },
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="2"
            />
            {errors.passengerCount && (
              <p className="text-red-500 text-sm mt-1">{errors.passengerCount.message}</p>
            )}
          </div>

          {/* 9. Assignment (Hidden from STAFF) */}
          {user?.role !== UserRole.STAFF && (
            <div className="border-t pt-4 sm:pt-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">{t('transfers.assignTo')}</h3>

              <div className="mb-4">
                <select
                  value={assignmentType}
                  onChange={(e) => {
                    setAssignmentType(e.target.value as typeof assignmentType);
                    if (e.target.value !== 'provider') setSelectedProvider(null);
                  }}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white min-h-[44px] text-sm sm:text-base"
                >
                  <option value="none">{t('transfers.weWillHandleIt')}</option>
                  <option value="driver">{t('transfers.ourOwnDriver')}</option>
                  <option value="provider">{t('transfers.providerWhatsappGroup')}</option>
                </select>
              </div>

              {assignmentType === 'none' && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Havuz Grubu</label>
                    <select
                      value={selectedGroupId}
                      onChange={e => setSelectedGroupId(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                    >
                      <option value="">🌐 Genel Havuz (Herkes görür)</option>
                      {providerGroups.filter(g => g.isActive).map(g => (
                        <option key={g.id} value={g.id}>🏊 {g.name}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                    {selectedGroupId
                      ? `Sadece "${providerGroups.find(g => g.id === selectedGroupId)?.name}" grubundaki tedarikçiler görür.`
                      : 'Tüm tedarikçiler görebilir, kim önce alırsa alır.'}
                  </p>
                </div>
              )}

              {assignmentType === 'driver' && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('transfers.selectDriver')}
                  </label>
                  <select
                    {...register('assignedDriverId')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('transfers.selectADriver')}</option>
                    {drivers.map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.fullName} ({driver.phone})
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('transfers.selectRegisteredDriver')}
                  </p>
                </div>
              )}

              {assignmentType === 'provider' && (
                <div className="bg-purple-50 p-4 rounded-lg space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('transfers.selectProvider')}
                    </label>
                    <select
                      {...register('assignedProviderId')}
                      onChange={(e) => {
                        const provider = providers.find(p => p.id === e.target.value);
                        setSelectedProvider(provider || null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">{t('transfers.selectAProvider')}</option>
                      {providers.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name} {provider.providerType === ProviderType.WHATSAPP ? '(WhatsApp)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedProvider?.providerType === ProviderType.WHATSAPP && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('transfers.driverName')}
                        </label>
                        <input
                          type="text"
                          {...register('providerDriverName')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder={t('transfers.placeholders.driverNameWhatsapp')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('transfers.driverPhone')}
                        </label>
                        <input
                          type="tel"
                          {...register('providerDriverPhone')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="+90 555 123 4567"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('transfers.licensePlate')}
                        </label>
                        <input
                          type="text"
                          {...register('providerPlate')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="34 ABC 123"
                        />
                      </div>
                      <p className="text-sm text-green-700">
                        {t('transfers.whatsappDriverHint')}
                      </p>
                    </>
                  )}

                  {selectedProvider && selectedProvider.providerType !== ProviderType.WHATSAPP && (
                    <p className="text-sm text-purple-700">
                      {t('transfers.providerFillInfo')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 10. Pricing & Customer */}
          {canEditPricing && (
            <div className="border-t pt-4 sm:pt-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">{t('transfers.pricing')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('transfers.currency')} *
                  </label>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value as Currency)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={Currency.TRY}>TRY (₺)</option>
                    <option value={Currency.EUR}>EUR (€)</option>
                    <option value={Currency.USD}>USD ($)</option>
                    <option value={Currency.GBP}>GBP (£)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('transfers.customerPrice')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">{currencySymbols[selectedCurrency]}</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      {...register('customerPrice', { valueAsNumber: true })}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('transfers.providerPrice')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">{currencySymbols[selectedCurrency]}</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      {...register('providerPrice', { valueAsNumber: true })}
                      disabled={!enableProviderPriceEdit}
                      className={`w-full pl-7 pr-3 py-2 border rounded-lg focus:ring-2 ${
                        !enableProviderPriceEdit
                          ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="mt-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableProviderPriceEdit}
                        onChange={(e) => setEnableProviderPriceEdit(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">{t('transfers.differentPrice')}</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('transfers.extraExpenses')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">{currencySymbols[selectedCurrency]}</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      {...register('extraExpenses', { valueAsNumber: true })}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Meet & Greet Fee */}
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <input
                      type="checkbox"
                      checked={hasMeetGreet}
                      onChange={(e) => {
                        setHasMeetGreet(e.target.checked);
                        if (!e.target.checked) setValue('meetGreetFee', undefined as any);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      {t('transfers.hasMeetGreet')}
                    </label>
                  </div>
                  {hasMeetGreet && (
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">&#8378;</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        {...register('meetGreetFee', { valueAsNumber: true })}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 11. Guests */}
          <div className="border-t pt-4 sm:pt-6">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">{t('transfers.guests')}</h3>
              <button
                type="button"
                onClick={() => setIsGuestModalOpen(true)}
                className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 font-medium text-sm sm:text-base min-h-[44px]"
              >
                {guests.length > 0 ? t('guest.editGuests') : `+ ${t('guest.addGuest')}`}
              </button>
            </div>

            {guests.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500">{t('transfers.noGuestsYet')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {guests.map((guest, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{guest.name}</p>
                      <div className="text-sm text-gray-600 space-x-3">
                        {guest.phone && <span>📞 {guest.phone}</span>}
                        {guest.email && <span>✉️ {guest.email}</span>}
                        {guest.tcPassport && <span>🆔 {guest.tcPassport}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveGuest(index)}
                      className="ml-4 text-red-600 hover:text-red-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 12. Special Notes */}
          <div className="border-t pt-4 sm:pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.specialNotes')}
            </label>
            <textarea
              {...register('specialNotes')}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('transfers.placeholders.specialNotes')}
            />
          </div>

          {/* 13. Status */}
          <div className="border-t pt-4 sm:pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.status')}
            </label>
            <select
              {...register('status')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="available">{t('transfers.statuses.available')}</option>
              <option value="claimed">{t('transfers.statuses.claimed')}</option>
              <option value="confirmed">{t('transfers.statuses.confirmed')}</option>
              <option value="in_progress">{t('transfers.statuses.inProgress')}</option>
              <option value="completed">{t('transfers.statuses.completed')}</option>
              <option value="cancelled">{t('transfers.statuses.cancelled')}</option>
            </select>
            {watchStatus === 'completed' && (
              (() => {
                const providerAssigned = !!(watchAssignedProviderId || watchWhatsappDriverName);
                const hasPP = watchProviderPrice != null && !isNaN(watchProviderPrice as any) && Number(watchProviderPrice) > 0;
                const ok = !providerAssigned || hasPP;
                return !ok ? (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    ⚠️ {t('transfers.completedRequiresPrices')}
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    ✓ {t('transfers.pricesOkCanComplete')}
                  </p>
                );
              })()
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 border-t pt-4 sm:pt-6">
            {canDeleteTransfer && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 sm:px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 font-medium min-h-[44px] order-last sm:order-first"
              >
                {t('transfers.deleteTransfer')}
              </button>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:ml-auto">
              <button
                type="button"
                onClick={() => navigate('/app/transfers')}
                className="px-4 sm:px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 font-medium min-h-[44px]"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 sm:px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 font-medium disabled:opacity-50 min-h-[44px]"
              >
                {loading ? t('transfers.updating') : t('transfers.updateTransfer')}
              </button>
            </div>
          </div>
        </form>
      </div>

      <GuestModal
        isOpen={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
        onGuestsAdded={handleGuestsAdded}
        existingGuests={guests}
      />
    </div>
  );
};
